import { toast } from 'sonner';
import { apiService } from '../../utils/api';
import type {
  PickerCategory,
  PickerItem,
  PickerSubmitResult,
} from '../../components/modals/classificationPicker';

const categories = {
  properties: {
    endpoint: 'add-properties',
    field: 'properties',
    itemField: 'classified_property_definition_id',
  },
  technicalReports: {
    endpoint: 'add-technical-reports',
    field: 'technical_reports',
    itemField: 'classified_technical_report_item_id',
  },
  products: {
    endpoint: 'add-reference-products',
    field: 'products',
    itemField: 'classified_product_id',
  },
  propertySets: {
    endpoint: 'apply-property-definition-set',
    multiEndpoint: 'add-set-property',
    itemField: 'classified_property_definition_set',
    multiField: 'classified_set_property_definition_id',
  },
  technicalReportSets: {
    endpoint: 'apply-technical-report-item-set',
    multiEndpoint: 'add-set-technical-report',
    itemField: 'classified_technical_report_item_set',
    multiField: 'classified_set_technical_report_item_id',
  },
} as const;

export const addTypemarkPickerItems =
  (
    target: { ids: string[]; template?: boolean; isCurrent?: () => boolean },
    category: PickerCategory,
    items: PickerItem[]
  ) =>
  async (dispatch, getState): Promise<PickerSubmitResult> => {
    const ids = [...new Set(target.ids)];
    const uniqueItems = [...new Map(items.map((item) => [String(item.id), item])).values()];
    if (!ids.length || !uniqueItems.length) return { error: 'Vyberte položky a cílový typemark.' };
    const multi = ids.length > 1;
    if (target.template && multi) return { error: 'Vyberte jednu šablonu.' };
    const resource = target.template ? 'instance-type-template' : 'instance-type';
    const base = `/api/v1/${resource}/${multi ? '' : `${ids[0]}/`}`;
    const contract = getState().contract;
    const config = {
      params: { contract: contract.contract?.id, phase: contract.phaseId, model: contract.modelId },
    };
    const type = target.template
      ? 'GET_TYPE_MARK_TEMPLATE_DETAIL_SUCCESS'
      : multi
      ? 'GET_SELECTED_TYPE_MARKS_DETAIL_SUCCESS'
      : 'GET_TYPE_MARK_DETAIL_SUCCESS';
    const completedIds: Array<string | number> = [];
    let latestDetail;
    const publish = (data) => {
      if (target.isCurrent?.() !== false) dispatch({ type, data });
    };
    const refresh = async () => {
      if (target.isCurrent?.() === false) return;
      const response = multi
        ? await apiService.post(
            '/api/v1/instance-type/common-data/',
            { instance_type_ids: ids },
            config
          )
        : await apiService.get(base, config);
      publish(response.data);
    };
    try {
      const definition = categories[category];
      if ('field' in definition) {
        const { data } = await apiService.patch(
          `${base}${definition.endpoint}/`,
          {
            [definition.field]: uniqueItems.map((item) => ({ [definition.itemField]: item.id })),
            ...(multi ? { instance_type_ids: ids } : {}),
            ...(category === 'properties' ? { update_existing_values: false } : {}),
          },
          config
        );
        completedIds.push(...uniqueItems.map((item) => item.id));
        latestDetail = data;
      } else {
        // Apply sets in selection order: overlapping sets must not race on ordering.
        for (const item of uniqueItems) {
          if (target.isCurrent?.() === false)
            return { completedIds, error: 'Cíl výběru se změnil.' };
          const { data } = await apiService.patch(
            `${base}${multi ? definition.multiEndpoint : definition.endpoint}/`,
            {
              [multi ? definition.multiField : definition.itemField]: item.id,
              ...(multi ? { instance_type_ids: ids } : {}),
            },
            config
          );
          completedIds.push(item.id);
          latestDetail = data;
        }
      }
      if (multi || !latestDetail) await refresh();
      else publish(latestDetail);
      if (target.isCurrent?.() !== false) toast.success(`Přidáno položek: ${uniqueItems.length}`);
      return { completedIds };
    } catch {
      let refreshed = true;
      try {
        await refresh();
      } catch {
        refreshed = false;
      }
      const failed = uniqueItems.find((item) => !completedIds.includes(item.id));
      return {
        completedIds,
        error:
          completedIds.length === uniqueItems.length
            ? 'Položky byly přidány, ale aktualizace náhledu selhala. Zavřete dialog a obnovte detail.'
            : `Přidáno ${completedIds.length} z ${uniqueItems.length}. ${
                failed?.title ? `Nepodařilo se dokončit „${failed.title}“. ` : ''
              }Zbývající výběr můžete potvrdit znovu.${
                refreshed
                  ? ''
                  : ' Nepodařilo se obnovit detail; před opakováním ověřte stav připojení.'
              }`,
      };
    }
  };
